"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import type { Matcher } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  /** Enable month/year dropdown (useful for DOB) */
  showDropdowns?: boolean
  /** Start year for dropdown (default: 1950) */
  startYear?: number
  /** End year for dropdown (default: current year) */
  endYear?: number
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Pick a date",
  disabled = false,
  minDate,
  maxDate,
  className,
  showDropdowns = false,
  startYear,
  endYear,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  // Build disabled matchers for react-day-picker v9
  const disabledDates: Matcher[] = []
  if (minDate) {
    disabledDates.push({ before: minDate })
  }
  if (maxDate) {
    disabledDates.push({ after: maxDate })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-12",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(newDate) => {
            onDateChange?.(newDate)
            setOpen(false)
          }}
          disabled={disabledDates.length > 0 ? disabledDates : undefined}
          captionLayout={showDropdowns ? "dropdown" : "label"}
          startYear={startYear}
          endYear={endYear}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}
